import type {
  FaireAcademyChapter,
  FaireAcademyGuide,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/types";
import { convertTitleToHash } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/utils";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import { IFaireAcademyGuide } from "@faire/web-api--source/indigofair/data/IFaireAcademyGuide";
import findRetailSpace from "@faire/web-api--source/routes/www/open-with-faire/academy/find-retail-space";
import { lazy } from "react";
import * as React from "react";

const LazyBudgeting = lazy(() =>
  import(
    /* webpackChunkName: "find-retail-space-budgeting" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/FindRetailSpace/__internal__/Budgeting"
  ).then((m) => ({ default: m.Budgeting }))
);
const LazyYourStoresLocation = lazy(() =>
  import(
    /* webpackChunkName: "find-retail-space-your-stores-location" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/FindRetailSpace/__internal__/YourStoresLocation"
  ).then((m) => ({ default: m.YourStoresLocation }))
);
const LazySigningALease = lazy(() =>
  import(
    /* webpackChunkName: "find-retail-space-signing-a-lease" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/FindRetailSpace/__internal__/SigningALease"
  ).then((m) => ({ default: m.SigningALease }))
);
const LazyPermits = lazy(() =>
  import(
    /* webpackChunkName: "find-retail-space-permits" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/FindRetailSpace/__internal__/Permits"
  ).then((m) => ({ default: m.Permits }))
);

const C1 = "Budgeting";
const BUDGETING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.BUDGETING,
  title: C1,
  readTimeMinutes: 3,
  titleHash: convertTitleToHash(C1),
  ChapterComponent: LazyBudgeting,
};

const C2 = "Your store’s location";
const YOUR_STORES_LOCATION: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.YOUR_STORE_LOCATION,
  title: C2,
  readTimeMinutes: 5,
  titleHash: convertTitleToHash(C2),
  ChapterComponent: LazyYourStoresLocation,
};

const C3 = "Signing a lease";
const SIGNING_A_LEASE: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.SIGNING_A_LEASE,
  title: C3,
  readTimeMinutes: 2,
  titleHash: convertTitleToHash(C3),
  ChapterComponent: LazySigningALease,
};

const C4 = "Permits";
const PERMITS: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.PERMITS,
  title: C4,
  readTimeMinutes: 2,
  titleHash: convertTitleToHash(C4),
  ChapterComponent: LazyPermits,
};

const GUIDE_CHAPTERS: Array<FaireAcademyChapter> = [
  BUDGETING,
  YOUR_STORES_LOCATION,
  SIGNING_A_LEASE,
  PERMITS,
];

export const FIND_A_RETAIL_LOCATION: FaireAcademyGuide = {
  id: IFaireAcademyGuide.GuideIdentifier.FIND_A_RETAIL_LOCATION,
  title: "Find a retail location",
  description: "Lock in the right space to bring your vision to life.",
  cardSummary: "Lock in the right space to bring your vision to life.",
  guideSummary: (
    <>
      Choosing the right location is pivotal for your retail store's success,
      and the costs associated with leasing your storefront can be substantial.
      In the course of selecting yours, you’ll want to study local foot traffic,
      demographics, competition, and proximity to suppliers. Budgeting is
      equally critical, and yours should include upfront costs, recurring costs,
      and renovation expenses. After all, very few storefronts will be turn-key
      ready—and that means permits and inspections will come into play. Follow
      our detailed framework for evaluating potential sites to find a location
      that’s optimized for customer accessibility, and ultimately, your store’s
      success.
    </>
  ),
  featuredImage:
    "https://cdn.faire.com/fastly/796c8a150594aeb252011d20a442e0ebf78e99665fb9439c7720f440a27b7364.png",
  featuredImageAltText:
    "Empty storefront location with blue door and empty plant beds",
  backgroundImage:
    "https://cdn.faire.com/fastly/3701d0450a1a9d082f36e2481edc6c18848c38aaf4258bd02fcfd64af92eac91.jpeg",
  readTimeMinutes: GUIDE_CHAPTERS.reduce(
    (partialReadTime, chapter) => partialReadTime + chapter.readTimeMinutes,
    0
  ),
  chapters: GUIDE_CHAPTERS,
  linkToPage: findRetailSpace(),
  titleTag: "How to find, budget for & close on a retail space",
};
